/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import {
	ChangeDetectionStrategy,
	Component,
	type ElementRef,
	ViewEncapsulation,
	forwardRef,
	input,
	output,
	viewChildren
} from "@angular/core";
import {
	type ControlValueAccessor,
	FormsModule,
	NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import { IconComponent } from "../icon/icon.component";

const noop = () => { };

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => CodeComponent),
	multi: true,
};

@Component({
	selector: "cb-code",

	imports: [FormsModule, IconComponent, NgxMaskDirective],
	templateUrl: "./code.component.html",
	styleUrl: "./code.component.scss",
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR, provideNgxMask()],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CodeComponent implements ControlValueAccessor {
	/**
	 * Label of the input
	 */
	public readonly label = input<string>();

	/**
	 * Hint of the input
	 */
	public readonly hint = input<string>();

	/**
	 * Info of the input displayed with a question mark
	 * when this is defined as trailing icon
	 */
	public readonly info = input<string>();

	/**
	 * If the input is required
	 */
	public readonly required = input<boolean>();

	/**
	 * If the input has an error
	 */
	public readonly error = input<boolean>();

	/**
	 * The length of the code
	 */
	public readonly length = input(6);

	/**
	 * If letters are allowed
	 */
	public readonly allowLetters = input(false);

	/**
	 * If the input is case sensitive
	 */
	public readonly caseSensitive = input(false);

	/**
	 * If the input is disabled
	 */
	public readonly disabled = input(false);

	/**
	 * Emits the value of the input on change
	 */
	public readonly change = output<string>();

	readonly inputElements = viewChildren<ElementRef>("CodeInput");

	/**
	 * The internal data model
	 */
	public values: { [key: string]: string } = {};

	/**
	 * Blurred inputs
	 */
	private blurredInputs: number[] = [];

	/**
	 * Returns an array of the length of the code
	 */
	public get inputs(): number[] {
		return Array(this.length());
	}

	/**
	 * Returns the value of the input
	 */
	public get value(): string {
		const keys = Object.keys(this.values).sort();

		return keys
			.map((key) =>
				this.caseSensitive() ? this.values[key] : this.values[key].toUpperCase(),
			)
			.join("");
	}

	public inputChanged(index: number): void {
		const inputElements = this.inputElements();
		if (inputElements?.at(index + 1)) {
			if (this.values[index]) {
				// Focus the next input
				inputElements.at(index + 1)?.nativeElement.focus();
			}
		}

		this.onChangeCallback(this.value);
		this.change.emit(this.value);
	}

	public inputKeydown(event: KeyboardEvent, index: number): void {
		let regexp: RegExp;

		if (this.allowLetters()) {
			// A regex that allows letters and numbers with a length of 1
			regexp = /^[a-zA-Z0-9]{1}$/;
		} else {
			// A regex that allows numbers with a length of 1
			regexp = /^[0-9]{1}$/;
		}

		// Test event.key against the regex
		if (regexp.test(event.key)) {
			if (this.values[index] !== undefined && this.values[index].length > 0) {
				this.values[index] = "";
			}
		} else {
			// If the key is arrow left focus the previous input
			// If the key is arrow right focus the next input
			const inputElements = this.inputElements();
			if (
				event.key === "ArrowLeft" &&
				inputElements &&
				inputElements.at(index - 1)
			) {
				inputElements.at(index - 1)?.nativeElement.focus();
			} else if (
				event.key === "ArrowRight" &&
				inputElements &&
				inputElements.at(index + 1)
			) {
				inputElements.at(index + 1)?.nativeElement.focus();
			}
		}
	}

	public inputDelete(event: Event, index: number): void {
		const inputElements = this.inputElements();
		if (inputElements?.at(index - 1)) {
			// Focus the previous input
			inputElements.at(index - 1)?.nativeElement.focus();
		}

		this.onChangeCallback(this.value);
		this.change.emit(this.value);
	}

	public inputBlurred(index: number): void {
		if (!this.blurredInputs.includes(index)) {
			this.blurredInputs.push(index);
		}

		if (this.blurredInputs.length === this.length()) {
			this.onTouchedCallback();
		}
	}

	public onPaste(event: ClipboardEvent): void {
		event.preventDefault();
		let pastedText = event.clipboardData?.getData("text");

		// Remove all the spaces
		pastedText = pastedText?.replace(/\s/g, "");

		let regexp: RegExp;

		if (this.allowLetters()) {
			// A regex that allows letters and numbers with a length of 1
			regexp = new RegExp(`^[a-zA-Z0-9]{${this.length()}}$`);
		} else {
			// A regex that allows numbers with a length of 1
			regexp = new RegExp(`^[0-9]{${this.length()}}$`);
		}

		if (pastedText?.match(regexp)) {
			const values = pastedText.split("");
			values.forEach((value, index) => {
				this.values[index] = value;
			});

			this.onChangeCallback(this.value);
			this.change.emit(this.value);
		}
	}

	public writeValue(obj: string): void {
		if (obj && obj !== this.value) {
			const values = obj.split("");
			values.forEach((value, index) => {
				this.values[index] = value;
			});
		}
	}

	private onTouchedCallback: () => void = noop;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	private onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}


}
